import { createAppAsyncThunk } from "appThunk";
import { logError } from "modules/helpers/logger/loggerSlice";
import { Partner } from "./partner";
import { Operator } from "./operator";
import { DateTime } from "luxon";
import { cubeProductQueryFilterHelper } from "modules/helpers/cube/cubeSlice";
import { ResultSet } from "@cubejs-client/core";
import { ProductSales } from "./productSales";
import { Store } from "./store";
import { Range } from "./storeRangeFilters/range";

export class Product {
    public readonly id: number;
    public readonly name: string;
    public readonly productCategory: string;
    public readonly sales: ProductSales;

    constructor(
        id: number,
        name: string,
        productCategory: string,
        clientSourcedSales: number,
        estimatedSales: number,
        optimisedSales: number,
        salesHeadroom: number,
        confidenceInterval?: number
    ) {
        this.id = id;
        this.name = name;
        this.productCategory = productCategory;
        this.sales = new ProductSales(
            String(id),
            name,
            clientSourcedSales,
            estimatedSales,
            optimisedSales,
            salesHeadroom,
            confidenceInterval
        );
    }
}

export const loadProducts = createAppAsyncThunk<
    Product[],
    { referenceDate: DateTime, partner?: Partner, selectedStore?: Store, selectedRange?: Range, operator?: Operator }
>(
    "customer/tools/product/loadProducts",
    async ({ referenceDate, partner, selectedStore, selectedRange, operator }, thunkAPI) => {
        try {
            if (!partner) {
                return [];
            }

            const startDate = referenceDate.minus({ months: 12 }).plus({ days: 1 }).startOf('day');
            const endDate = referenceDate.endOf('day');
            const query = {
                measures: [
                    "ProductSalesEstimate.SumClientSourcedSales",
                    "ProductSalesEstimate.SumEstimatedSales",
                    "ProductSalesEstimate.SumOptimisedSales",
                    "ProductSalesEstimate.SumSalesHeadroom",
                    "ProductSalesEstimate.AvgConfidenceInterval"
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    dateRange: [startDate, endDate]
                }],
                dimensions: [
                    "D_ProductCategory.PK_ProductCategory",
                    "D_ProductCategory.ProductCategory1",
                    "D_ProductCategory.ProductCategory2",
                ],
                filters: [],
                segments: [
                    "D_Store.OpenPhysicalStores"
                ],
            };

            const resultSet = await thunkAPI.dispatch(
                cubeProductQueryFilterHelper(query, operator, partner, selectedStore, selectedRange)
            ) as unknown as ResultSet;
            const products = resultSet.rawData().map(row => new Product(
                Number(row["D_ProductCategory.PK_ProductCategory"]),
                row["D_ProductCategory.ProductCategory2"],
                row["D_ProductCategory.ProductCategory1"],
                Number(row["ProductSalesEstimate.SumClientSourcedSales"]),
                Number(row["ProductSalesEstimate.SumEstimatedSales"]),
                Number(row["ProductSalesEstimate.SumOptimisedSales"]),
                Number(row["ProductSalesEstimate.SumSalesHeadroom"]),
                Number(row["ProductSalesEstimate.AvgConfidenceInterval"])
            ));
            return products;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading stores.", error));
            return thunkAPI.rejectWithValue(null);
        }
    }
);
